import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import {
    calculateDefaultValue,
    createTableFilters,
    PartialFormType,
} from "commons/hooks/useTableFilterBase";

import { FilterFieldCollection, FilterType } from "commons/types/table";
import { ObjectPropertyType } from "commons/types/validation";

import storage from "commons/utils/storage";

export type TableFilterWithMemoryAndButtonsHook = StaticTableFilterHook & {
    applyFilters: () => Array<FilterType>;
    clearFilters: () => Array<FilterType>;
    isFormChanged: boolean;
};

function useTableFilterWithMemoryAndButtons(
    key: string,
    fields: FilterFieldCollection
): TableFilterWithMemoryAndButtonsHook {
    const getKey = React.useCallback(() => `static-table-filter-saved-${key}`, [key]);

    const property: ObjectPropertyType = React.useMemo(
        () => ({
            type: "object",
            properties: {},
        }),
        []
    );

    const defaultValues = React.useMemo<PartialFormType>(
        () => calculateDefaultValue(fields),
        [fields]
    );

    const [initialValues, setInitialValues] = React.useState<PartialFormType>(
        storage.get(getKey()) ?? defaultValues
    );

    const [lastValues, setLastValues] = React.useState<PartialFormType>(initialValues);

    const { setValue, setForm, updateForm, form } = useForm<PartialFormType>(
        property,
        initialValues
    );

    const [filters, setFilters] = React.useState<Array<FilterType>>(
        createTableFilters(fields, form)
    );

    const applyFilters: () => Array<FilterType> = React.useCallback((): Array<FilterType> => {
        const newFilters = createTableFilters(fields, form);

        setFilters(newFilters);

        setIsFormChanged(false);

        setLastValues(form);

        storage.set(getKey(), form);

        return newFilters;
    }, [getKey, fields, form]);

    const clearFilters: () => Array<FilterType> = React.useCallback((): Array<FilterType> => {
        const newFilters = createTableFilters(fields, defaultValues);

        setFilters(newFilters);

        setForm(defaultValues);

        setIsFormChanged(false);

        setInitialValues(defaultValues);

        setLastValues(defaultValues);

        storage.set(getKey(), defaultValues);

        return newFilters;
    }, [getKey, setForm, fields, defaultValues]);

    const [isFormChanged, setIsFormChanged] = React.useState<boolean>(false);

    React.useEffect(() => setIsFormChanged(!_.isEqual(form, lastValues)), [form, lastValues]);

    return React.useMemo<TableFilterWithMemoryAndButtonsHook>(
        () => ({
            setValue,
            setForm,
            updateForm,
            applyFilters,
            clearFilters,
            clearStaticFilters: () => null,
            form,
            filters,
            isFormChanged,
        }),
        [setValue, setForm, updateForm, applyFilters, clearFilters, form, filters, isFormChanged]
    );
}

export default useTableFilterWithMemoryAndButtons;
